import { useState } from "react";
import { Link } from "react-router-dom";
import { Navigation, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Phone, CheckCircle } from "lucide-react";
import { SEO } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    try {
      await axios.post(`${API}/leads/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Contact Steward Lots for questions about buying or selling land. Christ-led land stewardship with integrity, transparency, and ethical land facilitation. We respond to every inquiry with honesty."
        path="/contact"
      />
      <Navigation mode="builder" />

      <div className="pt-24 pb-16 px-6 md:px-12 min-h-screen">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h1 className="font-serif text-3xl md:text-4xl text-[#1C1917] mb-6">
                Contact Steward Lots
              </h1>
              <p className="text-[#57534E] text-lg mb-8">
                Have a question about land stewardship, our process, or how we can serve you? 
                We'd love to hear from you. Send us a message and we'll respond as soon as possible.
              </p>

              {/* What to Expect Section */}
              <div className="mb-8">
                <h2 className="font-serif text-xl text-[#1C1917] mb-4">What to Expect</h2>
                <div className="text-[#57534E] space-y-3">
                  <p>
                    When you contact Steward Lots™, you can expect the same transparency and integrity 
                    that governs all our land transactions:
                  </p>
                  <ul className="space-y-2 ml-4">
                    <li className="flex gap-2">
                      <span className="text-[#BFA15F]" aria-hidden="true">•</span>
                      We respond within 24-48 business hours
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#BFA15F]" aria-hidden="true">•</span>
                      We answer questions honestly, even if it's not what you want to hear
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#BFA15F]" aria-hidden="true">•</span>
                      No pressure tactics or aggressive follow-ups
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#BFA15F]" aria-hidden="true">•</span>
                      Clear next steps if you decide to move forward
                    </li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F4] flex items-center justify-center" aria-hidden="true">
                    <Mail className="w-5 h-5 text-[#57534E]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1C1917]">Email</h3>
                    <a 
                      href="mailto:mark@stewardlots.org" 
                      className="text-[#57534E] hover:text-[#BFA15F] transition-colors"
                      aria-label="Email Steward Lots at mark@stewardlots.org"
                    >
                      mark@stewardlots.org
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F4] flex items-center justify-center" aria-hidden="true">
                    <Phone className="w-5 h-5 text-[#57534E]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#1C1917]">Phone</h3>
                    <a 
                      href="tel:+14408960199" 
                      className="text-[#57534E] hover:text-[#BFA15F] transition-colors"
                      aria-label="Call Steward Lots at 440-896-0199"
                    >
                      (440) 896-0199
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-[#F5F5F4] border border-[#E7E5E4]">
                <p className="font-serif italic text-[#57534E] mb-2">
                  "We respond to every message with the same integrity we bring to every deal."
                </p>
                <p className="text-sm text-[#A8A29E]">— Our Commitment</p>
              </div>

              {/* Quick Links */}
              <div className="mt-8">
                <h3 className="font-medium text-[#1C1917] mb-3">Looking for Something Specific?</h3>
                <div className="space-y-2 text-[#57534E]">
                  <p>
                    <Link to="/seller" className="text-[#BFA15F] hover:underline">Land Sellers</Link> — 
                    Learn how we help you sell your property with integrity
                  </p>
                  <p>
                    <Link to="/builder" className="text-[#BFA15F] hover:underline">Builders & Land Buyers</Link> — 
                    Find clean land opportunities with disclosed economics
                  </p>
                  <p>
                    <Link to="/faith" className="text-[#BFA15F] hover:underline">Faith & Principles</Link> — 
                    Understand our Scripture-based operating model
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="bg-[#F5F5F4] p-8 border border-[#E7E5E4] text-center" data-testid="contact-success">
                  <div className="w-16 h-16 bg-[#0F0F10] rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-white" aria-hidden="true" />
                  </div>
                  <h2 className="font-serif text-xl text-[#1C1917] mb-4">
                    Message Received
                  </h2>
                  <p className="text-[#57534E] mb-4">
                    Thank you for reaching out. We'll respond as soon as possible with the same 
                    transparency that governs all our work.
                  </p>
                  <p className="text-sm text-[#A8A29E]">
                    Expected response time: 24-48 business hours
                  </p>
                </div>
              ) : (
                <div>
                  <h2 className="font-serif text-2xl text-[#1C1917] mb-6">
                    Send Us a Message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        data-testid="input-name"
                        aria-required="true"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        data-testid="input-email"
                        aria-required="true"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="How can we help? Tell us about your land, your questions, or how you'd like to work together."
                        rows={5}
                        required
                        data-testid="input-message"
                        aria-required="true"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full bg-[#0F0F10] hover:bg-[#1C1917]"
                      disabled={loading}
                      data-testid="submit-btn"
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </Button>

                    <p className="text-xs text-[#A8A29E] text-center">
                      Your information is handled with the same integrity that governs our land transactions. 
                      See our <Link to="/privacy" className="underline hover:text-[#57534E]">Privacy Policy</Link>.
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
